import React, {
  FunctionComponent,
  useEffect,
  useState,
  createContext,
  useContext,
  ReactNode,
} from 'react'
import { Channel, Socket } from 'phoenix'
import { logger, LogContext } from '../../helpers/logger'
import { useAuth } from '../auth-context'

const MAX_SOCKET_RETRIES = 5

type SocketContextType = {
  socket: Socket | null
  teamChannel: Channel | null
  userChannel: Channel | null
  stakesChannel: Channel | null
  cmsChannel: Channel | null
  setUserIdForSocket: (userId: string | null) => void
  setTeamIdForSocket: (teamId: string | null) => void
  teamId: string
  userId: string
}

export const SocketContext = createContext<SocketContextType>({
  socket: null,
  teamChannel: null,
  userChannel: null,
  stakesChannel: null,
  cmsChannel: null,
  setUserIdForSocket: () => null,
  setTeamIdForSocket: () => null,
  teamId: '',
  userId: ''
})

type SocketProviderType = {
  wsUrl: string
  shouldConnect: boolean
  children: ReactNode
}

export const SocketProvider: FunctionComponent<SocketProviderType> = ({
  wsUrl,
  children,
  shouldConnect,
}) => {
  const [socket, setSocket] = useState<Socket | null>(null)
  const [userId, setUserIdForSocket] = useState<string | null>(null)
  const [teamId, setTeamIdForSocket] = useState<string | null>(null)
  const [userChannel, setUserChannel] = useState<Channel | null>(null)
  const [teamChannel, setTeamChannel] = useState<Channel | null>(null)
  const [stakesChannel, setStakesChannel] = useState<Channel | null>(null)
  const [cmsChannel, setCmsChannel] = useState<Channel | null>(null)
  const [retry, setRetry] = useState<number>(0)
  const { userId: authUserId } = useAuth()

  const connectionState = socket?.isConnected()

  useEffect(() => {
    if (shouldConnect && socket !== null) {
      socket.connect()
      setRetry(0)
    }
  }, [shouldConnect])

  useEffect(() => {
    const destroyConnection = () => {
      if (socket === null) return
      if (userChannel) {
        userChannel.leave()
        socket.remove(userChannel)
        setUserChannel(null)
      }
      if (teamChannel) {
        teamChannel.leave()
        socket.remove(teamChannel)
        setTeamChannel(null)
      }
      if (stakesChannel) {
        stakesChannel.leave()
        socket.remove(stakesChannel)
        setStakesChannel(null)
      }
      if (cmsChannel) {
        cmsChannel.leave()
        socket.remove(cmsChannel)
        setCmsChannel(null)
      }
      socket.disconnect(
        () => {},
        1000,
      )
      setSocket(null)
    }

    const establishConnection = () => {
      if (!wsUrl || !authUserId) {
        return
      }
      const s = new Socket(wsUrl, {
        params: { user_id: authUserId },
        transport: WebSocket,
        reconnectAfterMs: () => 1000,
      })
      try {
        s.connect()
      } catch (err) {
        logger.error(LogContext.PHEONIX_SOCKET, 'Socket connect threw', err, { retry })
      }
      s.onClose(() => {
        logger.info(LogContext.PHEONIX_SOCKET, 'Socket closed', { retry })
      })
      s.onOpen(() => {
        logger.info(LogContext.PHEONIX_SOCKET, 'Socket opened', { retry })
        setTimeout(() => {
          setSocket(s)
        }, 1)
      })
      setUserChannel(null)
      setTeamChannel(null)
      setStakesChannel(null)
      setCmsChannel(null)
      s.onError(err => {
        s.disconnect()

        if (retry === MAX_SOCKET_RETRIES) {
          // live updates stop here until the next page refresh
          logger.error(LogContext.PHEONIX_SOCKET, 'Socket retries exhausted', err, { retry })
        } else {
          logger.warn(LogContext.PHEONIX_SOCKET, 'Socket error, retrying', { retry, nextAttempt: retry + 1 }, err)
          setRetry(retry + 1)
        }
      })
    }

    establishConnection()

    return destroyConnection
  }, [retry, authUserId])

  useEffect(() => {
    if (!connectionState || !socket) {
      return
    }
    if (!userId) {
      if (!userChannel) return
      userChannel.leave()
      socket?.remove(userChannel)
      setUserChannel(null)
      return
    }
    const name = `user:${userId}`
    const newChannel = socket.channel(name)

    // the user channel is the overlay's only source of live bet updates, so a failed join is an error
    newChannel.join()
      .receive('ok', () => logger.info(LogContext.SOCKET, 'Channel joined', { channel: 'user' }))
      .receive('error', (reasons: any) =>
        logger.error(LogContext.SOCKET, 'Channel join failed', reasons, { channel: 'user' }))

    setUserChannel(newChannel)

    newChannel.onError((error: any) =>
      logger.warn(LogContext.SOCKET, 'Channel error', { channel: 'user' }, error)
    )
    return () => {
      logger.debug(LogContext.SOCKET, 'Channel left', { channel: 'user' })
      newChannel.leave()
      socket?.remove(newChannel)
      setUserChannel(null)
    }
  }, [userId, connectionState])

  useEffect(() => {
    if (!connectionState || !socket) {
      return
    }
    if (!teamId) {
      if (!teamChannel) return
      teamChannel.leave()
      socket?.remove(teamChannel)
      setTeamChannel(null)
      return
    }
    const name = `team:${teamId}`
    const newChannel = socket.channel(name)

    newChannel.join()
      .receive('ok', () => logger.debug(LogContext.SOCKET, 'Channel joined', { channel: 'team' }))
      .receive('error', (reasons: any) =>
        logger.warn(LogContext.SOCKET, 'Channel join failed', { channel: 'team' }, reasons))
    setTeamChannel(newChannel)

    newChannel.onError((error: any) =>
      logger.warn(LogContext.SOCKET, 'Channel error', { channel: 'team' }, error)
    )
    return () => {
      logger.debug(LogContext.SOCKET, 'Channel left', { channel: 'team' })
      newChannel.leave()
      socket?.remove(newChannel)
      setTeamChannel(null)
    }
  }, [teamId, connectionState])

  useEffect(() => {
    if (!connectionState || !socket) {
      return
    }
    const name = `stakes`
    const newChannel = socket.channel(name)

    newChannel.join()
      .receive('ok', () => logger.debug(LogContext.SOCKET, 'Channel joined', { channel: 'stakes' }))
      .receive('error', (reasons: any) =>
        logger.warn(LogContext.SOCKET, 'Channel join failed', { channel: 'stakes' }, reasons))
    setStakesChannel(newChannel)

    newChannel.onError((error: any) =>
      logger.warn(LogContext.SOCKET, 'Channel error', { channel: 'stakes' }, error)
    )
    return () => {
      logger.debug(LogContext.SOCKET, 'Channel left', { channel: 'stakes' })
      newChannel.leave()
      socket?.remove(newChannel)
      setStakesChannel(null)
    }
  }, [connectionState])

  useEffect(() => {
    if (!connectionState || !socket) {
      return
    }
    const name = `cms:publish`
    const newChannel = socket.channel(name)

    newChannel.join()
      .receive('ok', () => logger.debug(LogContext.SOCKET, 'Channel joined', { channel: 'cms' }))
      .receive('error', (reasons: any) =>
        logger.warn(LogContext.SOCKET, 'Channel join failed', { channel: 'cms' }, reasons))
    setCmsChannel(newChannel)

    newChannel.onError((error: any) =>
      logger.warn(LogContext.SOCKET, 'Channel error', { channel: 'cms' }, error)
    )
    return () => {
      logger.debug(LogContext.SOCKET, 'Channel left', { channel: 'cms' })
      newChannel.leave()
      socket?.remove(newChannel)
      setCmsChannel(null)
    }
  }, [connectionState])

  return (
    <SocketContext.Provider
      value={{
        socket,
        setUserIdForSocket,
        setTeamIdForSocket,
        teamChannel,
        userChannel,
        stakesChannel,
        cmsChannel,
        teamId: teamId || '',
        userId: userId || '',
      }}>
      {children}
    </SocketContext.Provider>
  )
}

export const useSocket = (): SocketContextType => useContext(SocketContext)
